import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Loader2, Sparkles, ShieldCheck, AlertTriangle, RotateCcw, Check, Camera, RefreshCw } from "lucide-react";

/**
 * IdPhotoEnhanceModal
 *
 * Full lifecycle for updating the Digital ID photo:
 *   1. User picks / drops an image.
 *   2. POST to /api/id/mine/photo/enhance which runs (a) Gemini vision
 *      compliance critique and (b) Nano Banana enhancement in one call.
 *   3. User sees ORIGINAL vs ENHANCED side-by-side plus the AI verdict.
 *   4. User picks which one to save. Chosen version is uploaded via
 *      /api/files/upload and pinned via PUT /api/id/profile.
 *
 * Props:
 *   open, onClose — modal control
 *   onSaved(user) — fires with fresh user object after successful save
 */
export default function IdPhotoEnhanceModal({ open, onClose, onSaved }) {
  const [step, setStep] = useState("pick");     // pick | analyzing | review | saving | done
  const [origPreview, setOrigPreview] = useState(null);
  const [origFile, setOrigFile] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [enhanced, setEnhanced] = useState(null);       // data URL
  const [choice, setChoice] = useState("enhanced");     // "enhanced" | "original"
  const inputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setStep("pick"); setOrigPreview(null); setOrigFile(null);
    setVerdict(null); setEnhanced(null); setChoice("enhanced");
  };

  const close = () => { reset(); onClose(); };

  const analyze = async (file) => {
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|webp|heic|heif)/.test(file.type)) {
      toast.error("Please pick a JPEG, PNG, WebP or HEIC image.");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Photo too large — max 12MB.");
      return;
    }
    setOrigFile(file);
    setOrigPreview(URL.createObjectURL(file));
    setStep("analyzing");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/id/mine/photo/enhance", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setVerdict(r.data);
      if (r.data.enhanced_base64) {
        setEnhanced(`data:${r.data.enhanced_mime || "image/png"};base64,${r.data.enhanced_base64}`);
        setChoice("enhanced");
      } else {
        setChoice("original");
      }
      setStep("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't analyze that photo. Try another one.");
      setStep("pick");
    }
  };

  const dataUrlToFile = async (dataUrl, name) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], name, { type: blob.type });
  };

  const save = async () => {
    setStep("saving");
    try {
      const file = choice === "enhanced" && enhanced
        ? await dataUrlToFile(enhanced, "pjp-id-photo.png")
        : origFile;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "id_photo");
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const put = await api.put("/id/profile", { id_photo_file_id: up.data.id });
      toast.success("Digital ID photo updated");
      setStep("done");
      onSaved?.(put.data.user);
      setTimeout(close, 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      setStep("review");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="idphoto-modal"
      onClick={close}
    >
      <div
        className="relative w-full max-w-3xl bg-obsidian border border-cyan/25 rounded-xl shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan" />
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan">// PJP · ID Photo · AI Review</div>
          </div>
          <button onClick={close} data-testid="idphoto-close" className="text-white/60 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {step === "pick" && (
            <PickStep inputRef={inputRef} onPick={analyze} onOpenCamera={() => setStep("camera")} />
          )}

          {step === "camera" && (
            <CameraStep onCapture={(file) => analyze(file)} onBack={() => setStep("pick")} />
          )}

          {step === "analyzing" && (
            <div className="flex flex-col items-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-cyan mb-3" />
              <div className="mono text-[11px] tracking-widest uppercase text-white/70">AI is reviewing your photo…</div>
              <div className="text-xs text-white/45 mt-1.5">Compliance check + enhancement + background replacement</div>
              {origPreview && (
                <img src={origPreview} alt="" className="mt-4 h-40 w-40 object-cover rounded-lg border border-white/10" />
              )}
            </div>
          )}

          {step === "review" && verdict && (
            <ReviewStep
              verdict={verdict}
              origPreview={origPreview}
              enhanced={enhanced}
              choice={choice}
              setChoice={setChoice}
              onRedo={() => reset()}
              onSave={save}
            />
          )}

          {step === "saving" && (
            <div className="flex flex-col items-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-cyan mb-3" />
              <div className="mono text-[11px] tracking-widest uppercase text-white/70">Saving to your Digital ID…</div>
            </div>
          )}

          {step === "done" && (
            <div className="flex flex-col items-center py-10">
              <div className="h-12 w-12 rounded-full bg-avail/15 border border-avail/40 grid place-items-center">
                <Check className="h-5 w-5 text-avail" />
              </div>
              <div className="mono text-[11px] tracking-widest uppercase text-white/70 mt-3">Photo updated</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PickStep({ inputRef, onPick, onOpenCamera }) {
  const [drag, setDrag] = useState(false);
  return (
    <div>
      {/* Two big options: take a fresh photo OR upload one */}
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <button
          type="button"
          onClick={onOpenCamera}
          data-testid="idphoto-open-camera"
          className="group cursor-pointer rounded-lg border-2 border-cyan/30 bg-cyan/[0.04] hover:bg-cyan/[0.08] hover:border-cyan/60 transition-all p-6 text-center"
        >
          <Camera className="h-8 w-8 text-cyan mx-auto mb-2" />
          <div className="font-display text-lg font-semibold text-white">Take a photo</div>
          <div className="text-[12px] text-white/55 mt-1">Uses your camera — front or rear</div>
          <div className="mono text-[10px] tracking-widest uppercase text-cyan/70 mt-3">// Best quality</div>
        </button>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); onPick(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="idphoto-dropzone"
          className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            drag ? "border-cyan bg-cyan/[0.06]" : "border-white/15 hover:border-cyan/40 hover:bg-white/[0.02]"
          }`}
        >
          <Upload className="h-8 w-8 text-white/60 mx-auto mb-2" />
          <div className="font-display text-lg font-semibold text-white">Upload from library</div>
          <div className="text-[12px] text-white/55 mt-1">JPG · PNG · WebP · HEIC · up to 12MB</div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/40 mt-3">// Drag & drop supported</div>
        </div>
      </div>
      <input
        ref={inputRef}
        data-testid="idphoto-file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0])}
      />

      <div className="mt-5 grid md:grid-cols-3 gap-3 text-[11px] text-white/60">
        <Tip title="Face visible" body="Front-facing, eyes open, no sunglasses, no hats." />
        <Tip title="Solo shot" body="One person only. Foreground focus." />
        <Tip title="Good light" body="Even lighting, no motion blur, in focus." />
      </div>
    </div>
  );
}

function Tip({ title, body }) {
  return (
    <div className="rounded border border-white/10 bg-white/[0.02] p-2.5">
      <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">{title}</div>
      <div className="text-[11px] text-white/55 mt-0.5">{body}</div>
    </div>
  );
}

function ReviewStep({ verdict, origPreview, enhanced, choice, setChoice, onRedo, onSave }) {
  const acceptable = verdict.acceptable;
  return (
    <div>
      {/* Verdict banner */}
      <div
        data-testid="idphoto-verdict"
        className={`rounded-lg p-3 mb-4 flex items-start gap-2.5 border ${
          acceptable ? "border-avail/40 bg-avail/[0.06]" : "border-amber-400/40 bg-amber-400/[0.06]"
        }`}
      >
        {acceptable ? <ShieldCheck className="h-4 w-4 text-avail mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-amber-300 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className={`mono text-[10px] tracking-widest uppercase ${acceptable ? "text-avail" : "text-amber-300"}`}>
            {acceptable ? "ID-worthy" : "Needs a better shot"}
          </div>
          <div className="text-sm text-white mt-0.5">{verdict.reason}</div>
          {(verdict.issues || []).length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {verdict.issues.map((i, ix) => (
                <li key={ix} className="text-[11px] text-white/60 flex gap-1.5">
                  <span className="text-amber-300">•</span>{i}
                </li>
              ))}
            </ul>
          )}
          {(verdict.suggestions || []).length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {verdict.suggestions.map((s, ix) => (
                <li key={ix} className="text-[11px] text-white/45 flex gap-1.5">
                  <span className="text-cyan/60">·</span>{s}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Side-by-side previews */}
      <div className="grid grid-cols-2 gap-3">
        <PreviewChip
          label="Original"
          testId="idphoto-orig"
          selected={choice === "original"}
          disabled={false}
          onClick={() => setChoice("original")}
          src={origPreview}
        />
        <PreviewChip
          label="AI-Enhanced"
          testId="idphoto-enhanced"
          selected={choice === "enhanced"}
          disabled={!enhanced}
          onClick={() => enhanced && setChoice("enhanced")}
          src={enhanced}
          empty={!enhanced ? "Enhancer skipped this one" : null}
          badge="Nano Banana"
        />
      </div>

      {/* Actions */}
      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onRedo}
          data-testid="idphoto-redo"
          className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/20 text-white/70 hover:bg-white/5 flex items-center gap-1.5"
        >
          <RotateCcw className="h-3 w-3" /> Try another
        </button>
        <button
          type="button"
          onClick={onSave}
          data-testid="idphoto-save"
          disabled={choice === "enhanced" && !enhanced}
          className="mono text-[10px] tracking-widest uppercase px-4 py-2 rounded bg-cyan text-obsidian font-bold hover:bg-cyan/90 disabled:opacity-40 flex items-center gap-1.5"
        >
          <Check className="h-3 w-3" /> Use {choice === "enhanced" ? "enhanced" : "original"} photo
        </button>
      </div>
    </div>
  );
}

function PreviewChip({ label, testId, selected, disabled, onClick, src, empty, badge }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`relative rounded-lg overflow-hidden border-2 transition-all text-left ${
        selected ? "border-cyan shadow-[0_0_20px_rgba(75,156,255,0.25)]" : "border-white/10 hover:border-white/25"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div className="aspect-square w-full bg-black/40 flex items-center justify-center">
        {src ? (
          <img src={src} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="text-[11px] text-white/40 mono uppercase tracking-widest px-4 text-center">{empty || "—"}</div>
        )}
      </div>
      <div className="absolute top-2 left-2 flex items-center gap-1.5">
        <span className={`mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded ${
          selected ? "bg-cyan text-obsidian" : "bg-black/60 text-white/70 border border-white/15"
        }`}>{label}</span>
        {badge && !disabled && (
          <span className="mono text-[8px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-black/70 border border-cyan/30 text-cyan/80">
            {badge}
          </span>
        )}
      </div>
      {selected && !disabled && (
        <div className="absolute top-2 right-2 bg-cyan text-obsidian rounded-full p-1">
          <Check className="h-3 w-3" />
        </div>
      )}
    </button>
  );
}

/**
 * CameraStep — live camera preview via getUserMedia. Works on desktop
 * (webcam) and mobile (phone camera). Includes a face-alignment ring, camera
 * flip for phones (front↔rear), and a big shutter button.
 */
function CameraStep({ onCapture, onBack }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [facing, setFacing] = useState("user");   // "user" (front/selfie) | "environment" (rear)
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [snapping, setSnapping] = useState(false);
  const streamRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      setError(null);
      setReady(false);
      // Stop any existing tracks before requesting a new stream.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width:  { ideal: 1280 },
            height: { ideal: 1280 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        setError(
          e?.name === "NotAllowedError"
            ? "Camera permission denied. Enable it in your browser and try again."
            : e?.name === "NotFoundError"
              ? "No camera found on this device."
              : `Camera error: ${e?.message || e?.name || "unknown"}`
        );
      }
    };
    start();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [facing]);

  const snap = async () => {
    if (!videoRef.current || !ready) return;
    setSnapping(true);
    try {
      const v = videoRef.current;
      const w = v.videoWidth || 1024;
      const h = v.videoHeight || 1024;
      // Center-square crop for a passport-style output.
      const side = Math.min(w, h);
      const sx = (w - side) / 2;
      const sy = (h - side) / 2;
      const canvas = canvasRef.current || document.createElement("canvas");
      canvas.width = side;
      canvas.height = side;
      const ctx = canvas.getContext("2d");
      // Front-camera preview is mirrored via CSS; when saving we want the
      // TRUE (un-mirrored) frame so the enhancer sees the real orientation.
      ctx.drawImage(v, sx, sy, side, side, 0, 0, side, side);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      if (!blob) throw new Error("Capture failed");
      const file = new File([blob], `pjp-id-camera-${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture(file);
    } catch (e) {
      toast.error("Couldn't capture — try again");
      setSnapping(false);
    }
  };

  const flip = () => setFacing((f) => (f === "user" ? "environment" : "user"));

  return (
    <div>
      <div
        className="relative rounded-lg overflow-hidden bg-black border border-white/10"
        style={{ aspectRatio: "1 / 1", maxWidth: 480, margin: "0 auto" }}
      >
        {/* Live video — mirrored when using front camera so it feels like a mirror. */}
        <video
          ref={videoRef}
          data-testid="idphoto-camera-video"
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* Face-alignment ring */}
        {ready && !snapping && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className="rounded-full border-2 border-cyan/70"
              style={{ width: "62%", height: "78%", boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)" }}
            />
          </div>
        )}

        {/* Corner brackets */}
        {ready && (
          <>
            <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-cyan" />
            <span className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-cyan" />
            <span className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-cyan" />
            <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-cyan" />
          </>
        )}

        {/* Overlay states */}
        {!ready && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70">
            <Loader2 className="h-6 w-6 animate-spin text-cyan mb-2" />
            <div className="mono text-[10px] tracking-widest uppercase">Waking camera…</div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 p-6 flex flex-col items-center justify-center text-center">
            <AlertTriangle className="h-6 w-6 text-amber-300 mb-2" />
            <div className="text-sm text-white/85 max-w-sm">{error}</div>
          </div>
        )}
      </div>

      {/* Camera controls */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="idphoto-cam-back"
          className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/20 text-white/70 hover:bg-white/5"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={snap}
          disabled={!ready || snapping}
          data-testid="idphoto-cam-shutter"
          className="relative h-16 w-16 rounded-full bg-white grid place-items-center disabled:opacity-40 transition-transform hover:scale-105 active:scale-95"
          aria-label="Capture photo"
        >
          <span className="absolute inset-1.5 rounded-full border-2 border-obsidian" />
          <span className="absolute inset-3 rounded-full bg-white" />
          {snapping && <Loader2 className="relative h-5 w-5 animate-spin text-obsidian" />}
        </button>
        <button
          type="button"
          onClick={flip}
          disabled={!ready}
          data-testid="idphoto-cam-flip"
          className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/20 text-white/70 hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-40"
          title="Switch camera"
        >
          <RefreshCw className="h-3 w-3" /> Flip
        </button>
      </div>
      <div className="text-center text-[11px] text-white/45 mt-3">
        Line your face up with the ring · tap the shutter · we'll AI-review it next
      </div>
    </div>
  );
}

